import axios from "axios";

// Use empty string for relative URLs - works on any domain (preview & production)
const API_URL = "";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeader() };
  return config;
});

// Courses
export const getCourses = () => api.get("/courses");

// Gate Pass
export const createGatePass = (data) => api.post("/gate-pass/create", data);
export const getMyRequests = () => api.get("/gate-pass/my-requests");
export const getGatePass = (id) => api.get(`/gate-pass/${id}`);
export const getGatePassByNumber = (number) => api.get(`/gate-pass/by-number/${number}`);
export const markPrinted = (id) => api.post(`/gate-pass/${id}/mark-printed`);

// Coordinator
export const getCoordinatorPending = () => api.get("/coordinator/pending");
export const coordinatorAction = (id, data) => api.post(`/coordinator/action/${id}`, data);
export const getCoordinatorHistory = () => api.get("/coordinator/history");

// Principal
export const getPrincipalPending = () => api.get("/principal/pending");
export const principalAction = (id, data) => api.post(`/principal/action/${id}`, data);

// Vice Principal
export const getVPPending = () => api.get("/vice-principal/pending");
export const vpAction = (id, data) => api.post(`/vice-principal/action/${id}`, data);

// Hostel Warden
export const getWardenPending = () => api.get("/warden/pending");
export const wardenAction = (id, data) => api.post(`/warden/action/${id}`, data);

// Gate Operator
export const gateSearch = (q) => api.get(`/gate/search?q=${encodeURIComponent(q)}`);
export const getReadyForVerification = () => api.get("/gate/ready-for-verification");
export const verifyGatePass = (id, data) => api.post(`/gate/verify/${id}`, data);
export const getVerifiedToday = () => api.get("/gate/verified-today");

// Admin
export const getAllUsers = () => api.get("/admin/users");
export const createUser = (data) => api.post("/admin/users", data);
export const updateUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/admin/users/${id}`);
export const setUserAvailability = (id, available) => api.put(`/admin/availability/${id}?is_available=${available}`);
export const getAllRequests = (params) => api.get("/admin/all-requests", { params });
export const getDashboardStats = () => api.get("/admin/dashboard-stats");
export const getAuditLogs = (limit = 100) => api.get(`/admin/audit-logs?limit=${limit}`);

// Reports
export const getDateWiseReport = (startDate, endDate) => 
  api.get(`/reports/date-wise?start_date=${startDate}&end_date=${endDate}`);
export const getCourseWiseReport = () => api.get("/reports/course-wise");
export const getYearWiseReport = () => api.get("/reports/year-wise");
export const getRejectionReport = () => api.get("/reports/rejection-reasons");
export const getParentCallReport = () => api.get("/reports/parent-call");
export const getDailyPassesReport = (date) => api.get(`/reports/daily-passes?date=${date}`);
export const exportReportExcel = (startDate, endDate) => 
  api.get(`/reports/export-excel?start_date=${startDate}&end_date=${endDate}`, { responseType: 'blob' });

// Today/Past passes for all dashboards
export const getTodaySummary = () => api.get("/passes/today-summary");
export const getPassesByDate = (date) => api.get(`/passes/by-date?date=${date}`);
export const getGatePastDate = (date) => api.get(`/gate/past-date?date=${date}`);

// Warden return entry
export const getWardenActivePasses = () => api.get("/warden/active-passes");
export const getWardenClosedPasses = (date) => api.get(`/warden/closed-passes${date ? `?date=${date}` : ""}`);
export const markStudentReturned = (passId, data) => api.post(`/warden/return-entry/${passId}`, data);
export const markReturnEntry = markStudentReturned;

// Admin forward to VP
export const adminForwardToVP = (passId) => api.post(`/admin/forward-to-vp/${passId}`);
export const adminForwardCoordinatorPass = (passId, target) => api.post(`/admin/forward-coordinator-pass/${passId}`, { target });

// Password management
export const changePassword = (data) => api.post("/auth/change-password", data);
export const adminResetPassword = (data) => api.post("/admin/reset-password", data);

// Notification Settings
export const getNotificationSettings = () => api.get("/admin/notification-settings");
export const updateNotificationSettings = (data) => api.put("/admin/notification-settings", data);
export const getNotificationTemplates = () => api.get("/admin/notification-templates");
export const updateNotificationTemplate = (templateId, data) => api.put(`/admin/notification-templates/${templateId}`, data);
export const getNotificationLogs = (limit = 100, requestNumber = null) => {
  let url = `/admin/notification-logs?limit=${limit}`;
  if (requestNumber) url += `&request_number=${requestNumber}`;
  return api.get(url);
};
export const retryNotification = (logId) => api.post(`/admin/notification-retry/${logId}`);
export const testNotification = (phoneNumber, channel = "whatsapp") => 
  api.post(`/admin/test-notification?phone_number=${encodeURIComponent(phoneNumber)}&channel=${channel}`);

// Student Registration (Public endpoints - no auth)
export const lookupStudent = (registrationNumber) => 
  api.get(`/student/lookup?registration_number=${encodeURIComponent(registrationNumber)}`);
export const searchStudents = (query) => 
  api.get(`/student/search?q=${encodeURIComponent(query)}`);
export const studentRequestPass = (data) => 
  api.post("/student/request-pass", data);
export const getStudentRequests = (registrationNumber) => 
  api.get(`/student/my-requests?registration_number=${encodeURIComponent(registrationNumber)}`);

// Admin Student Management
export const getAllStudents = () => api.get("/admin/students");
export const createStudent = (data) => api.post("/admin/students", data);
export const generateRegistrationNumber = (courseId, year) => 
  api.post(`/admin/students/generate-registration?course_id=${courseId}&year=${year}`);
export const bulkUploadStudents = (students) => 
  api.post("/admin/students/bulk-upload", { students });
export const updateStudent = (id, params) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      queryParams.append(key, params[key]);
    }
  });
  return api.put(`/admin/students/${id}?${queryParams.toString()}`);
};
export const deleteStudent = (id) => api.delete(`/admin/students/${id}`);

// Admin create pass for blocked students
export const adminCreatePassForStudent = (data) => api.post("/admin/create-pass-for-student", data);

// Approval timeline report
export const getApprovalTimelineReport = (startDate, endDate) => api.get(`/reports/approval-timeline?start_date=${startDate}&end_date=${endDate}`);
